import type { ReactNode } from 'react';

export interface TabItem {
  /** Stable identifier for the tab — used for the active comparison. */
  id: string;
  label: string;
  /** Optional badge (e.g. count) shown to the right of the label. */
  badge?: ReactNode;
  /** When true the tab renders disabled (e.g. waiting on backing data). */
  disabled?: boolean;
}

export interface TabBarProps {
  tabs: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  /** ARIA label for the tablist. Defaults to "Tabs". */
  ariaLabel?: string;
}

export function TabBar({ tabs, activeId, onChange, ariaLabel = 'Tabs' }: TabBarProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex flex-wrap gap-1 border-b border-base-300"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={tab.disabled}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && onChange(tab.id)}
            className={`
              px-4 py-2 text-sm font-medium transition-colors
              border-b-2 -mb-px
              ${
                isActive
                  ? 'border-primary text-primary'
                  : tab.disabled
                    ? 'border-transparent text-base-content/40 cursor-not-allowed'
                    : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
              }
            `}
          >
            <span>{tab.label}</span>
            {tab.badge !== undefined && (
              <span className="ml-2 inline-flex items-center justify-center rounded-full bg-base-200 px-2 py-0.5 text-xs">
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
